import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Upload, 
  X, 
  FileText, 
  File, 
  Image as ImageIcon,
  Archive
} from 'lucide-react';

const FileUpload = ({ onFileUpload, disabled = false, accept = "*/*", multiple = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getFileIcon = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
      return ImageIcon;
    } else if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return FileText;
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return Archive;
    } else {
      return File;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (multiple) {
        setSelectedFiles(files);
      } else {
        setSelectedFile(files[0]);
        setSelectedFiles([]);
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      if (multiple) {
        setSelectedFiles(files);
      } else {
        setSelectedFile(files[0]);
        setSelectedFiles([]);
      }
    }
  };

  const handleUpload = async () => {
    const filesToUpload = multiple ? selectedFiles : [selectedFile];
    if (!filesToUpload.length || !onFileUpload) return;

    setUploading(true);
    try {
      if (multiple) {
        // Upload each file individually
        for (const file of filesToUpload) {
          await onFileUpload(file);
        }
        setSelectedFiles([]);
      } else {
        await onFileUpload(selectedFile);
        setSelectedFile(null);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      {/* Drag and Drop Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : (selectedFile || selectedFiles.length > 0)
              ? 'border-green-500 bg-green-50'
              : 'border-slate-300 hover:border-slate-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <Input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          accept={accept}
          multiple={multiple}
          className="hidden"
          disabled={disabled}
        />
        
        {multiple && selectedFiles.length > 0 ? (
          <div className="space-y-2">
            <div className="text-sm font-medium text-green-700">
              {selectedFiles.length} files selected:
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  {React.createElement(getFileIcon(file.name), {
                    className: "w-4 h-4 text-green-600"
                  })}
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium text-slate-900">
                      {file.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : selectedFile ? (
          <div className="flex items-center justify-center space-x-3">
            {React.createElement(getFileIcon(selectedFile.name), {
              className: "w-8 h-8 text-green-600"
            })}
            <div className="text-left">
              <div className="font-medium text-slate-900 truncate max-w-48">
                {selectedFile.name}
              </div>
              <div className="text-sm text-slate-500">
                {formatFileSize(selectedFile.size)}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-8 h-8 text-slate-400 mx-auto" />
            <div className="text-slate-600">
              <span className="font-medium">Click to upload</span> or drag and drop
            </div>
            <div className="text-xs text-slate-500">
              {multiple ? 'Select multiple files' : 'Any file type supported'}
            </div>
          </div>
        )}
      </div>

      {/* Upload Controls */}
      {(selectedFile || selectedFiles.length > 0) && (
        <div className="flex items-center space-x-2">
          <Button
            onClick={handleUpload}
            disabled={uploading || disabled}
            size="sm"
            className="flex-1"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : (multiple ? `Upload ${selectedFiles.length} Files` : 'Upload File')}
          </Button>
          <Button
            onClick={handleClear}
            disabled={uploading || disabled}
            variant="outline"
            size="sm"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;